import { useContext, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { SectionContext } from "./section";

/* ---------- icons: the closed line set, 20-unit grid, 1.6 stroke ---------- */
const I = ({ d, children }: { d?: string; children?: ReactNode }) => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {d ? <path d={d} /> : children}
  </svg>
);
export const Icon = {
  home: () => <I d="M3 9.5 10 4l7 5.5V16a1 1 0 0 1-1 1h-4v-5H8v5H4a1 1 0 0 1-1-1V9.5z" />,
  run: () => <I><rect x="3" y="4" width="14" height="13" rx="2" /><path d="M3 8h14M7 2.5V5M13 2.5V5" /></I>,
  clipboard: () => <I><rect x="4" y="3.5" width="12" height="14" rx="2" /><path d="M7.5 3.5h5v2h-5zM7 9.5h6M7 12.5h4" /></I>,
  people: () => <I><circle cx="8" cy="7" r="3" /><path d="M2.5 16.5a5.5 5.5 0 0 1 11 0M13 4.5a2.5 2.5 0 0 1 0 5M15 11.5a4.5 4.5 0 0 1 2.5 4" /></I>,
  heart: () => <I d="M10 16.5s-6-3.8-6-8.2A3.3 3.3 0 0 1 10 6a3.3 3.3 0 0 1 6 2.3c0 4.4-6 8.2-6 8.2z" />,
  doc: () => <I><path d="M5 3h7l4 4v10a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" /><path d="M12 3v4h4M7 10h6M7 13h6" /></I>,
  camera: () => <I><path d="M3 7.5a1.5 1.5 0 0 1 1.5-1.5h2l1-2h5l1 2h2A1.5 1.5 0 0 1 17 7.5v7a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 3 14.5z" /><circle cx="10" cy="11" r="3" /></I>,
  image: () => <I><rect x="3" y="4" width="14" height="12" rx="2" /><circle cx="7.5" cy="8.5" r="1.3" /><path d="m3.5 14 4-4 3 3 2.5-2.5L17 14" /></I>,
  tag: () => <I><path d="M3 10.5V4h6.5l7 7-6.5 6.5z" /><circle cx="6.5" cy="7.5" r="1" /></I>,
  gear: () => <I><circle cx="10" cy="10" r="2.5" /><path d="M10 2.5v2M10 15.5v2M2.5 10h2M15.5 10h2M4.7 4.7l1.4 1.4M13.9 13.9l1.4 1.4M4.7 15.3l1.4-1.4M13.9 6.1l1.4-1.4" /></I>,
  search: () => <I><circle cx="9" cy="9" r="5.5" /><path d="m13.5 13.5 3.5 3.5" /></I>,
  back: () => <I d="M12.5 4 6.5 10l6 6" />,
  chevR: () => <I d="m7.5 4 6 6-6 6" />,
  chevD: () => <I d="m4 7.5 6 6 6-6" />,
  chevL: () => <I d="m12.5 4-6 6 6 6" />,
  external: () => <I><path d="M11 4h5v5M16 4l-7 7" /><path d="M14 11.5V15a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h3.5" /></I>,
  menu: () => <I d="M3 6h14M3 10h14M3 14h14" />,
  check: () => <I d="m4 10.5 4 4 8-9" />,
  mark: () => <I><path d="M4 16 10 4l6 12" /><path d="M6.5 12h7" /></I>,
  info: () => <I><circle cx="10" cy="10" r="7" /><path d="M10 9v4.5M10 6.5v.5" /></I>,
  alert: () => <I><path d="M10 3.5 17 16H3z" /><path d="M10 8.5v3.5M10 14v.5" /></I>,
  pages: () => <I><rect x="3" y="3.5" width="14" height="13" rx="2" /><path d="M3 8h14M8 8v8.5" /></I>,
  layout: () => <I><rect x="3" y="3.5" width="14" height="13" rx="2" /><path d="M3 8h14M3 12.25h14" /></I>,
  close: () => <I d="m5 5 10 10M15 5 5 15" />,
  plus: () => <I d="M10 4v12M4 10h12" />,
  trash: () => <I><path d="M4 6h12M8 6V4h4v2M6 6l.7 10h6.6L14 6" /></I>,
  link: () => <I><path d="M8.5 11.5a3 3 0 0 0 4.2 0l2.3-2.3a3 3 0 0 0-4.2-4.2l-1 1" /><path d="M11.5 8.5a3 3 0 0 0-4.2 0L5 10.8a3 3 0 0 0 4.2 4.2l1-1" /></I>,
  listBullet: () => <I><path d="M7 5.5h10M7 10h10M7 14.5h10" /><circle cx="3.5" cy="5.5" r="1" fill="currentColor" /><circle cx="3.5" cy="10" r="1" fill="currentColor" /><circle cx="3.5" cy="14.5" r="1" fill="currentColor" /></I>,
  listNumber: () => <I><path d="M8 5.5h9M8 10h9M8 14.5h9" /><path d="M2.5 4.5 4 3.5v4M2.5 12h2.5l-2.5 3h2.5" /></I>,
  eraser: () => <I><path d="m4 13 7-7 4.5 4.5-4 4H7.5z" /><path d="M3 17h14" /></I>,
  code: () => <I d="m7 6-4 4 4 4M13 6l4 4-4 4" />,
  key: () => <I><circle cx="7" cy="12.5" r="3.5" /><path d="M9.6 10 17 2.6M14 5.5l2 2M16 3.5l2 2" /></I>,
  receipt: () => <I><path d="M5 2.5h10v15l-2-1.3-1.7 1.3-1.3-1.3-1.3 1.3L7 16.2 5 17.5z" /><path d="M8 7h4M8 10h4" /></I>,
  percent: () => <I><path d="m5 15 10-10" /><circle cx="6" cy="6" r="1.8" /><circle cx="14" cy="14" r="1.8" /></I>,
  chart: () => <I><path d="M3 3v14h14" /><path d="m6 12 3-3 3 2 4-5" /></I>,
  truck: () => <I><path d="M2.5 5h9v8h-9zM11.5 8h3l3 3v2h-6" /><circle cx="6" cy="14.5" r="1.5" /><circle cx="14.5" cy="14.5" r="1.5" /></I>,
  copy: () => <I><rect x="7" y="7" width="10" height="10" rx="1.5" /><path d="M13 7V4.5A1.5 1.5 0 0 0 11.5 3h-7A1.5 1.5 0 0 0 3 4.5v7A1.5 1.5 0 0 0 4.5 13H7" /></I>,
  more: () => <I><circle cx="10" cy="4.5" r="1.2" /><circle cx="10" cy="10" r="1.2" /><circle cx="10" cy="15.5" r="1.2" /></I>,
  lock: () => <I><rect x="4.5" y="9" width="11" height="8" rx="1.5" /><path d="M7 9V6.5a3 3 0 0 1 6 0V9" /></I>,
  eye: () => <I><path d="M2 10s3-5.5 8-5.5 8 5.5 8 5.5-3 5.5-8 5.5S2 10 2 10z" /><circle cx="10" cy="10" r="2.5" /></I>,
  card: () => <I><rect x="2.5" y="5" width="15" height="10.5" rx="1.5" /><path d="M2.5 8.5h15M5.5 12.5h3" /></I>,
  mail: () => <I><rect x="2.5" y="4.5" width="15" height="11" rx="1.5" /><path d="m3 5.5 7 5.5 7-5.5" /></I>,
  bank: () => <I><path d="M2.5 7.5 10 3.5l7.5 4" /><path d="M4.5 8.5v6M8.2 8.5v6M11.8 8.5v6M15.5 8.5v6" /><path d="M2.5 16.5h15" /></I>,
  store: () => <I><path d="M3.5 8.5V16a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1V8.5" /><path d="M2.5 8.5 4 3.5h12l1.5 5z" /><path d="M8 17v-4.5h4V17" /></I>,
  star: () => <I d="m10 3 2.1 4.4 4.8.6-3.5 3.3.9 4.8L10 13.8 5.7 16.1l.9-4.8L3.1 8l4.8-.6z" />,
  box: () => <I><path d="m10 2.5 7 3.5v8l-7 3.5L3 14V6z" /><path d="m3 6 7 3.5L17 6M10 9.5v8" /></I>,
  refresh: () => <I><path d="M16.5 10a6.5 6.5 0 1 1-1.9-4.6" /><path d="M16.5 3.5v3h-3" /></I>,
  archive: () => <I><rect x="3" y="4" width="14" height="4" rx="1" /><path d="M4.5 8v7.5A1.5 1.5 0 0 0 6 17h8a1.5 1.5 0 0 0 1.5-1.5V8M8.5 11h3" /></I>,
  bolt: () => <I d="M11 2.5 4.5 11H10l-1 6.5L15.5 9H10z" />,
  clock: () => <I><circle cx="10" cy="10" r="7" /><path d="M10 6v4l2.5 2" /></I>,
  branch: () => <I><circle cx="5.5" cy="4.5" r="1.8" /><circle cx="5.5" cy="15.5" r="1.8" /><circle cx="14.5" cy="7" r="1.8" /><path d="M5.5 6.3v7.4M14.5 8.8c0 3-4 3-7.5 5" /></I>,
  flag: () => <I><path d="M5 17.5V3" /><path d="M5 3.5h9l-2 3.5 2 3.5H5" /></I>,
  share: () => <I><circle cx="5" cy="10" r="2.2" /><circle cx="15" cy="4.5" r="2.2" /><circle cx="15" cy="15.5" r="2.2" /><path d="m7 9 6-3.4M7 11l6 3.4" /></I>,
  megaphone: () => <I><path d="M3.5 8v4h3l6.5 3.5v-11L6.5 8z" /><path d="M6.5 12 8 16.5h2.2L9 12M15.5 7.5a3 3 0 0 1 0 5" /></I>,
  briefcase: () => <I><rect x="3" y="6.5" width="14" height="10" rx="1.5" /><path d="M7.5 6.5V5a1.5 1.5 0 0 1 1.5-1.5h2A1.5 1.5 0 0 1 12.5 5v1.5M3 11h14" /></I>,
  pulse: () => <I d="M2.5 10.5h3.2l2-5 4.2 9.5 2.2-4.5h3.4" />,
  // Meetings: two people talking (two speech shapes, one behind the other), on the line set's grid and stroke.
  meeting: () => (
    <I>
      <path d="M7.5 7V5A1.5 1.5 0 0 1 9 3.5h7A1.5 1.5 0 0 1 17.5 5v5a1.5 1.5 0 0 1-1.5 1.5h-1V14l-2.5-2.5" />
      <path d="M4 7.5h7A1.5 1.5 0 0 1 12.5 9v4.5A1.5 1.5 0 0 1 11 15H7.5L5 17.5V15H4a1.5 1.5 0 0 1-1.5-1.5V9A1.5 1.5 0 0 1 4 7.5z" />
    </I>
  ),
  comment: () => <I d="M4 4h12a1.5 1.5 0 0 1 1.5 1.5v7.5a1.5 1.5 0 0 1-1.5 1.5H9.5L6 17.5v-3H4A1.5 1.5 0 0 1 2.5 13V5.5A1.5 1.5 0 0 1 4 4z" />,
  video: () => <I><rect x="3" y="4.5" width="14" height="11" rx="2" /><path d="m8.5 7.6 4 2.4-4 2.4z" /></I>,
  desktop: () => <I><rect x="2.5" y="3.5" width="15" height="10" rx="1.5" /><path d="M10 13.5v3M7 16.5h6" /></I>,
  phone: () => <I><rect x="5.5" y="2.5" width="9" height="15" rx="2" /><path d="M9 14.5h2" /></I>,
  minus: () => <I d="M4 10h12" />,
  locate: () => <I><circle cx="10" cy="10" r="5.5" /><circle cx="10" cy="10" r="1.5" /><path d="M10 2v2.5M10 15.5V18M2 10h2.5M15.5 10H18" /></I>,
  pause: () => <I d="M7.5 5v10M12.5 5v10" />,
  play: () => <I d="M7 4.5v11l8.5-5.5z" />,
  expand: () => <I d="M3.5 8V3.5H8M12 3.5h4.5V8M16.5 12v4.5H12M8 16.5H3.5V12" />,
  collapse: () => <I d="M8 3.5V8H3.5M16.5 8H12V3.5M12 16.5V12h4.5M3.5 12H8v4.5" />,
  bell: () => <I><path d="M5 13.5V9a5 5 0 0 1 10 0v4.5l1.5 2h-13z" /><path d="M8.5 17.5a1.6 1.6 0 0 0 3 0" /></I>,
  shield: () => <I d="M10 2.5 16 5v4.5c0 3.8-2.6 6.6-6 8-3.4-1.4-6-4.2-6-8V5z" />,
  plug: () => <I><path d="M7 2.5v4M13 2.5v4M5 6.5h10v3a5 5 0 0 1-10 0z" /><path d="M10 14.5v3" /></I>,
  grid: () => <I><rect x="3" y="3" width="6" height="6" rx="1.5" /><rect x="11" y="3" width="6" height="6" rx="1.5" /><rect x="3" y="11" width="6" height="6" rx="1.5" /><rect x="11" y="11" width="6" height="6" rx="1.5" /></I>,
  folder: () => <I d="M3 5.5A1.5 1.5 0 0 1 4.5 4h3.5l1.5 2h6A1.5 1.5 0 0 1 17 7.5v7a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 3 14.5z" />,
  sparkle: () => <I d="M10 3c.6 3.4 2.6 5.4 6 6-3.4.6-5.4 2.6-6 6-.6-3.4-2.6-5.4-6-6 3.4-.6 5.4-2.6 6-6z" />,
};

/* ---------- small pieces ---------- */
export function Badge({ tone = "neutral", children }: { tone?: "neutral" | "success" | "info" | "warn" | "accent" | "error"; children: ReactNode }) {
  return <span className={`badge${tone !== "neutral" ? ` badge--${tone}` : ""}`}>{children}</span>;
}

export function stateTone(state: string): "neutral" | "success" | "info" | "warn" | "accent" | "error" {
  switch (state) {
    case "live":
    case "published":
    case "publish":
    case "approved":
    case "registered":
    case "completed":
      return "success";
    case "scheduled":
    case "going_live":
      return "info";
    case "incomplete":
      return "warn";
    case "past":
    case "awaiting_email_confirmation":
    case "pending":
      return "warn";
    case "postponed":
      return "accent";
    case "cancelled":
    case "deleted":
    case "withdrawn":
      return "error";
    default:
      return "neutral";
  }
}

export function label(s: string | null | undefined): string {
  if (!s) return "";
  const words = s.replace(/[_-]+/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function Card({ title, action, children, flush, foot, id }: { title?: string; action?: ReactNode; children: ReactNode; flush?: boolean; foot?: ReactNode; id?: string }) {
  return (
    <section id={id} className={`card${flush ? " card--flush" : ""}`}>
      {(title || action) && (
        <div className="card__head">
          {title && <h2>{title}</h2>}
          {action}
        </div>
      )}
      <div className="card__body">{children}</div>
      {foot && <div className="card__foot">{foot}</div>}
    </section>
  );
}

/**
 * A section of an account page, to the familiar admin's account settings: what it is and
 * why on the left, its card on the right; one column on a phone.
 */
export function Annotated({ title, badge, text, children }: { title: string; badge?: ReactNode; text?: ReactNode; children: ReactNode }) {
  return (
    <section className="annotated">
      <div className="annotated__info">
        <h2>
          {title}
          {badge}
        </h2>
        {text && <p>{text}</p>}
      </div>
      <div className="annotated__body">{children}</div>
    </section>
  );
}

export function Page({ title, sub, back, badge, actions, narrow, wide, children }: { title: ReactNode; sub?: ReactNode; back?: string; badge?: ReactNode; actions?: ReactNode; narrow?: boolean; wide?: boolean; children: ReactNode }) {
  const nav = useNavigate();
  const section = useContext(SectionContext);
  const SectionIcon = section?.icon;
  return (
    <div className={`page${narrow ? " page--narrow" : ""}${wide ? " page--wide" : ""}`}>
      <div className="page__head">
        {/* The section's icon heads the title; on a record's page it leads back, then a › before the record's name. */}
        {back ? (
          SectionIcon ? (
            <>
              <button type="button" className="page__back page__crumb" aria-label="Back" title={`Back to ${section.label}`} onClick={() => nav(back)}>
                <SectionIcon />
              </button>
              <span className="page__chev" aria-hidden="true">
                <Icon.chevR />
              </span>
            </>
          ) : (
            <button type="button" className="page__back" aria-label="Back" onClick={() => nav(back)}>
              <Icon.back />
            </button>
          )
        ) : SectionIcon ? (
          <span className="page__icon" aria-hidden="true">
            <SectionIcon />
          </span>
        ) : null}
        <h1 className="page__title">
          {title}
          {badge}
        </h1>
        {actions && <div className="page__actions">{actions}</div>}
      </div>
      {sub && <p className="page__sub">{sub}</p>}
      {children}
    </div>
  );
}

export function Thumb({ src, alt = "", contain, icon, muted }: { src: string | null | undefined; alt?: string; contain?: boolean; icon?: ReactNode; muted?: boolean }) {
  return <span className={`cell__thumb${contain ? " cell__thumb--contain" : ""}${muted ? " cell__thumb--muted" : ""}`}>{src ? <img src={src} alt={alt} loading="lazy" /> : (icon ?? <Icon.image />)}</span>;
}

/** A button that opens a short list of actions (the familiar admin's action list): closes on a choice, a click outside or Escape. */
export function Menu({ label: text, items, primary, disabled, icon, compact, ariaLabel }: { label: ReactNode; items: { key: string; label: ReactNode; hint?: string; danger?: boolean; onSelect: () => void }[]; primary?: boolean; disabled?: boolean; icon?: ReactNode; compact?: boolean; ariaLabel?: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const [place, setPlace] = useState<CSSProperties | undefined>(undefined);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  // The list sits over the page (fixed), so a list in a card that scrolls sideways is never cut off: under the
  // button, or above it when there's no room below; from the button's left edge when the button is on the left of
  // the screen, else to its right edge. It follows the button when the page scrolls or resizes.
  useLayoutEffect(() => {
    if (!open) {
      setPlace(undefined);
      return;
    }
    const put = () => {
      const b = button.current?.getBoundingClientRect();
      const l = list.current;
      if (!b || !l) return;
      const w = l.offsetWidth;
      const h = l.offsetHeight;
      const vw = window.innerWidth;
      const below = b.bottom + 6 + h <= window.innerHeight - 8 || b.top - 6 - h < 8;
      const fromLeft = b.left + b.width / 2 < vw / 2;
      const left = Math.max(8, Math.min(fromLeft ? b.left : b.right - w, vw - w - 8));
      setPlace({ position: "fixed", top: below ? b.bottom + 6 : b.top - 6 - h, left, right: "auto", bottom: "auto" });
    };
    put();
    let frame = 0;
    const follow = (e: Event) => {
      if (e.type === "scroll" && e.target instanceof Node && list.current?.contains(e.target)) return;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(put);
    };
    window.addEventListener("resize", follow);
    window.addEventListener("scroll", follow, true);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", follow);
      window.removeEventListener("scroll", follow, true);
    };
  }, [open]);
  return (
    <div className="menu" ref={ref}>
      <button ref={button} type="button" className={compact ? "btn btn--sm btn--icon" : `btn${primary ? " btn--primary" : ""}`} aria-haspopup="menu" aria-expanded={open} aria-label={ariaLabel} disabled={disabled} onClick={() => setOpen((o) => !o)}>
        {icon}
        {compact ? null : text}
        {compact ? null : <Icon.chevD />}
      </button>
      {open && (
        <div className="menu__list" role="menu" ref={list} style={place}>
          {items.map((it) => (
            <button
              key={it.key}
              type="button"
              role="menuitem"
              className={it.danger ? "is-danger" : undefined}
              onClick={() => {
                setOpen(false);
                it.onSelect();
              }}
            >
              <span>{it.label}</span>
              {it.hint && <small className="muted">{it.hint}</small>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function Empty({ title, text }: { title: string; text?: string }) {
  return (
    <div className="empty">
      <h3>{title}</h3>
      {text && <p>{text}</p>}
    </div>
  );
}

/** Rows picked in a list, by id. Only the rows shown count, so a new page or tab starts with none picked. */
export function useSelection(ids: string[]) {
  const [picked, setPicked] = useState<ReadonlySet<string>>(() => new Set());
  const chosen = ids.filter((id) => picked.has(id));
  return {
    ids: chosen,
    count: chosen.length,
    has: (id: string) => picked.has(id),
    all: ids.length > 0 && chosen.length === ids.length,
    some: chosen.length > 0,
    toggle: (id: string) =>
      setPicked((old) => {
        const next = new Set(old);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      }),
    setAll: (on: boolean) => setPicked(on ? new Set(ids) : new Set()),
    clear: () => setPicked(new Set()),
  };
}
export type Selection = ReturnType<typeof useSelection>;

/** The box at the start of a row; a click on it never opens the row. */
export function SelectCell({ sel, id, label }: { sel: Selection; id: string; label: string }) {
  return (
    <td className="table__select" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
      <input type="checkbox" aria-label={`Select ${label}`} checked={sel.has(id)} onChange={() => sel.toggle(id)} />
    </td>
  );
}

/**
 * A list's header row, the familiar admin's: the select-all box, then the column names; once rows are picked, a bar
 * over the column names says how many and offers what can be done to them all (the columns keep their widths).
 */
export function SelectHead({ sel, actions, children }: { sel: Selection; actions: ReactNode; children: ReactNode }) {
  const row = useRef<HTMLTableRowElement>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const r = row.current;
    if (!sel.some || !r) return;
    const measure = () => setWidth(r.offsetWidth - (r.cells[0]?.offsetWidth ?? 0));
    measure();
    const watch = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    watch?.observe(r);
    return () => watch?.disconnect();
  }, [sel.some]);
  return (
    <thead>
      <tr ref={row} className={sel.some ? "table__bulk" : undefined}>
        <th className="table__select">
          <input
            type="checkbox"
            aria-label={sel.all ? "Clear the selection" : "Select all on this page"}
            checked={sel.all}
            ref={(el) => {
              if (el) el.indeterminate = sel.some && !sel.all;
            }}
            onChange={(e) => sel.setAll(e.target.checked)}
          />
          {sel.some && (
            <div className="table__bulk-bar" role="toolbar" aria-label="Selected rows" style={{ width }}>
              <span>{sel.count} selected</span>
              <span className="table__bulk-actions">{actions}</span>
            </div>
          )}
        </th>
        {children}
      </tr>
    </thead>
  );
}

export function Pager({ page, totalPages, total, onPage }: { page: number; totalPages: number; total: number; onPage: (p: number) => void }) {
  if (totalPages <= 1) return <div className="pager">{total === 1 ? "1 item" : `${total} items`}</div>;
  return (
    <div className="pager">
      <button type="button" className="btn" disabled={page <= 1} onClick={() => onPage(page - 1)} aria-label="Previous page">
        <Icon.chevL />
      </button>
      <span>
        Page {page} of {totalPages} · {total} items
      </span>
      <button type="button" className="btn" disabled={page >= totalPages} onClick={() => onPage(page + 1)} aria-label="Next page">
        <Icon.chevR />
      </button>
    </div>
  );
}

export function Tabs<T extends string>({ tabs, value, onChange }: { tabs: { key: T; label: string; count?: number }[]; value: T; onChange: (k: T) => void }) {
  return (
    <div className="tabs" role="tablist">
      {tabs.map((t) => (
        <button key={t.key} type="button" role="tab" aria-selected={t.key === value} className={t.key === value ? "is-active" : ""} onClick={() => onChange(t.key)}>
          {t.label}
          {typeof t.count === "number" && <span>{t.count}</span>}
        </button>
      ))}
    </div>
  );
}

export function SearchBox({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder: string }) {
  return (
    <label className="search">
      <Icon.search />
      <input type="search" value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}

/* ---------- formatting, in the business's own time zone and language ---------- */
let TZ = "Australia/Sydney";
let LOCALE = "en-AU";
/** Set once a business is open: dates and times on every page read in its zone. */
export function setBusinessClock(timeZone: string, locale: string): void {
  TZ = timeZone || TZ;
  LOCALE = locale || LOCALE;
}
export function businessZone(): string {
  return TZ;
}
export function fmtDate(v: string | null | undefined, opts: Intl.DateTimeFormatOptions = { day: "numeric", month: "short", year: "numeric" }): string {
  if (!v) return "";
  let s = String(v).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return new Intl.DateTimeFormat(LOCALE, { timeZone: "UTC", ...opts }).format(new Date(`${s}T12:00:00Z`));
  else {
    s = s.replace(" ", "T");
    if (/[+-]\d{2}$/.test(s)) s += ":00";
    if (!/(Z|[+-]\d{2}:\d{2})$/.test(s)) s += "Z";
  }
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return String(v);
  return new Intl.DateTimeFormat(LOCALE, { timeZone: TZ, ...opts }).format(d);
}
export function fmtDateTime(v: string | null | undefined): string {
  return fmtDate(v, { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" });
}
export function fmtTime(hm: string | null | undefined): string {
  if (!hm) return "";
  const [h, m] = hm.split(":").map(Number);
  if (h === undefined || Number.isNaN(h)) return hm;
  const suffix = h >= 12 ? "pm" : "am";
  const hour = h % 12 === 0 ? 12 : h % 12;
  return m ? `${hour}:${String(m).padStart(2, "0")}${suffix}` : `${hour}${suffix}`;
}
export function fmtBytes(n: number | string | null | undefined): string {
  const v = Number(n ?? 0);
  if (!v) return "";
  if (v < 1024) return `${v} B`;
  if (v < 1024 * 1024) return `${(v / 1024).toFixed(0)} KB`;
  return `${(v / 1024 / 1024).toFixed(1)} MB`;
}
export function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}
